import { MESSAGE_TYPE } from 'shared/constants/messages';
import { showBadgeOnFavicon } from './faviconHelper';
import { initFaviconSwitcher } from './faviconHelper';

import { EVENT_TYPES } from 'dashboard/routes/dashboard/settings/profile/constants.js';
import GlobalStore from 'dashboard/store';
import AudioNotificationStore from './AudioNotificationStore';
import {
  isConversationAssignedToMe,
  isConversationUnassigned,
  isMessageFromCurrentUser,
} from './AudioMessageHelper';
import WindowVisibilityHelper from './WindowVisibilityHelper';
import { useAlert } from 'dashboard/composables';
import {
  isActiveWhatsAppRinging,
  whatsappCallStatusTimestamp,
} from 'dashboard/helper/whatsappCallStatus';

const NOTIFICATION_TIME = 30000;
const ALERT_DURATION = 10000;
const ALERT_PATH_PREFIX = '/audio/dashboard/';
const CALL_ALERT_PATH_PREFIX = '/audio/whatsapp/';
const DEFAULT_TONE = 'ding';
const DEFAULT_CALL_TONE = 'anos90';
const DEFAULT_ALERT_TYPE = ['none'];

export class DashboardAudioNotificationHelper {
  constructor(store) {
    if (!store) {
      throw new Error('store is required');
    }
    this.store = new AudioNotificationStore(store);

    this.notificationConfig = {
      audioAlertType: DEFAULT_ALERT_TYPE,
      playAlertOnlyWhenHidden: true,
      alertIfUnreadConversationExist: false,
    };

    this.recurringNotificationTimer = null;

    this.audioConfig = {
      audio: null,
      tone: DEFAULT_TONE,
      hasSentSoundPermissionsRequest: false,
    };

    this.callAudioConfig = {
      audio: null,
      tone: DEFAULT_CALL_TONE,
      activeConversationIds: new Set(),
      recurringTimer: null,
      hasSentSoundPermissionsRequest: false,
    };

    this.currentUser = null;
  }

  intializeAudio = () => {
    const resourceUrl = `${ALERT_PATH_PREFIX}${this.audioConfig.tone}.mp3`;
    this.audioConfig.audio = new Audio(resourceUrl);
    return this.audioConfig.audio.load();
  };

  initializeCallAudio = () => {
    const resourceUrl = `${CALL_ALERT_PATH_PREFIX}${this.callAudioConfig.tone}.mp3`;
    this.callAudioConfig.audio = new Audio(resourceUrl);
    return this.callAudioConfig.audio.load();
  };

  playAudioAlert = async () => {
    try {
      await this.audioConfig.audio.play();
    } catch (error) {
      if (
        error.name === 'NotAllowedError' &&
        !this.hasSentSoundPermissionsRequest
      ) {
        this.hasSentSoundPermissionsRequest = true;
        useAlert(
          'PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.SOUND_PERMISSION_ERROR',
          { usei18n: true, duration: ALERT_DURATION }
        );
      }
    }
  };

  playCallAudioAlert = async () => {
    if (!this.callAudioConfig.audio) {
      this.initializeCallAudio();
    }

    try {
      await this.callAudioConfig.audio.play();
    } catch (error) {
      if (
        error.name === 'NotAllowedError' &&
        !this.callAudioConfig.hasSentSoundPermissionsRequest
      ) {
        this.callAudioConfig.hasSentSoundPermissionsRequest = true;
        useAlert(
          'PROFILE_SETTINGS.FORM.AUDIO_NOTIFICATIONS_SECTION.SOUND_PERMISSION_ERROR',
          { usei18n: true, duration: ALERT_DURATION }
        );
      }
    }
  };

  set = ({
    currentUser,
    alwaysPlayAudioAlert,
    alertIfUnreadConversationExist,
    audioAlertType = DEFAULT_ALERT_TYPE,
    audioAlertTone = DEFAULT_TONE,
    callAlertTone = DEFAULT_CALL_TONE,
  }) => {
    this.notificationConfig = {
      ...this.notificationConfig,
      audioAlertType: audioAlertType.split('+').filter(Boolean),
      playAlertOnlyWhenHidden: !alwaysPlayAudioAlert,
      alertIfUnreadConversationExist: alertIfUnreadConversationExist,
    };

    this.currentUser = currentUser;

    const previousAudioTone = this.audioConfig.tone;
    this.audioConfig = {
      ...this.audioConfig,
      tone: audioAlertTone,
    };

    if (previousAudioTone !== audioAlertTone) {
      this.intializeAudio();
    }

    const previousCallAudioTone = this.callAudioConfig.tone;
    this.callAudioConfig = {
      ...this.callAudioConfig,
      tone: callAlertTone || DEFAULT_CALL_TONE,
    };

    if (previousCallAudioTone !== this.callAudioConfig.tone) {
      this.initializeCallAudio();
    }

    this.stopCallRingtoneLoop();
    initFaviconSwitcher();
    this.clearRecurringTimer();
    this.playAudioEvery30Seconds();
    this.syncActiveCallRingtonesFromStore();
  };

  // eslint-disable-next-line class-methods-use-this
  extractConversationId = payload => {
    return payload?.conversation_id || payload?.id;
  };

  getConversationFromStore = conversationId => {
    if (!conversationId) return null;
    const conversationGetter = this.store?.store?.getters?.getConversationById;
    if (typeof conversationGetter !== 'function') return null;
    return conversationGetter(conversationId);
  };

  getInboxFromMessage = message => {
    const inboxGetter = this.store?.store?.getters?.['inboxes/getInbox'];
    if (typeof inboxGetter !== 'function') return {};

    return inboxGetter(message?.inbox_id) || {};
  };

  isInternalGroupMessage = message => {
    const inbox = this.getInboxFromMessage(message);
    const conversation = this.getConversationFromStore(
      this.extractConversationId(message)
    );
    const conversationAttributes = conversation?.additional_attributes;

    return (
      inbox?.channel_type === 'Channel::Internal' &&
      !inbox.internal_direct_messages_host &&
      inbox.internal_channel_role !== 'service_desk' &&
      !conversationAttributes?.internal_conversation_type &&
      !conversationAttributes?.service_ticket_id &&
      message?.message_type === MESSAGE_TYPE.OUTGOING &&
      !message?.private &&
      message?.sender?.type === 'user'
    );
  };

  isCurrentUserInternalGroupMember = message => {
    const memberIds = this.getInboxFromMessage(message)?.member_ids || [];
    return memberIds.map(Number).includes(Number(this.currentUser?.id));
  };

  extractConversationAssigneeId = payload => {
    const payloadAssigneeId =
      payload?.conversation?.assignee_id ||
      payload?.meta?.assignee?.id ||
      payload?.assignee?.id ||
      payload?.assignee_id;
    if (payloadAssigneeId) return payloadAssigneeId;

    const conversationId = this.extractConversationId(payload);
    const storeConversation = this.getConversationFromStore(conversationId);
    return storeConversation?.meta?.assignee?.id || null;
  };

  resolveCallStatus = payload => {
    const customStatus = payload?.custom_attributes?.whatsapp_last_call_status;
    if (customStatus) return customStatus;

    const messageCallData = payload?.additional_attributes?.whatsapp_call;
    if (messageCallData) {
      return (
        messageCallData.status || messageCallData.state || messageCallData.event
      );
    }
    const conversationId = this.extractConversationId(payload);
    const storeConversation = this.getConversationFromStore(conversationId);
    return (
      storeConversation?.custom_attributes?.whatsapp_last_call_status || null
    );
  };

  resolveCallTimestamp = payload => {
    const conversationTimestamp =
      payload?.custom_attributes?.whatsapp_last_call_at;
    if (conversationTimestamp) return conversationTimestamp;

    const messageCallData = payload?.additional_attributes?.whatsapp_call;
    if (messageCallData) {
      return whatsappCallStatusTimestamp(
        messageCallData,
        payload?.created_at || payload?.updated_at
      );
    }

    const conversationId = this.extractConversationId(payload);
    const storeConversation = this.getConversationFromStore(conversationId);
    return storeConversation?.custom_attributes?.whatsapp_last_call_at || null;
  };

  resolveConversationStatus = payload => {
    if (payload?.status && payload?.custom_attributes) return payload.status;
    if (payload?.conversation?.status) return payload.conversation.status;

    const conversationId = this.extractConversationId(payload);
    return this.getConversationFromStore(conversationId)?.status;
  };

  isConversationAssignedToCurrentUser = payload => {
    const assigneeId = this.extractConversationAssigneeId(payload);
    const currentUserId = this.currentUser?.id;
    if (!assigneeId || !currentUserId) return false;
    return Number(assigneeId) === Number(currentUserId);
  };

  startCallRingingForConversation = conversationId => {
    if (!conversationId) return;

    this.callAudioConfig.activeConversationIds.add(conversationId);
    if (this.callAudioConfig.recurringTimer) return;

    this.playCallAudioAlert();
    this.callAudioConfig.recurringTimer = setInterval(() => {
      [...this.callAudioConfig.activeConversationIds].forEach(activeId => {
        const conversation = this.getConversationFromStore(activeId);
        if (conversation) this.evaluateCallRingtone(conversation);
      });
      if (this.callAudioConfig.activeConversationIds.size === 0) {
        this.stopCallRingtoneLoop();
        return;
      }
      this.playCallAudioAlert();
    }, 5000);
  };

  stopCallRingingForConversation = conversationId => {
    if (!conversationId) return;
    this.callAudioConfig.activeConversationIds.delete(conversationId);
    if (this.callAudioConfig.activeConversationIds.size === 0) {
      this.stopCallRingtoneLoop();
    }
  };

  stopCallRingtoneLoop = () => {
    if (this.callAudioConfig.recurringTimer) {
      clearInterval(this.callAudioConfig.recurringTimer);
      this.callAudioConfig.recurringTimer = null;
    }
    this.callAudioConfig.activeConversationIds.clear();
    if (this.callAudioConfig.audio && !this.callAudioConfig.audio.paused) {
      this.callAudioConfig.audio.pause();
      this.callAudioConfig.audio.currentTime = 0;
    }
  };

  evaluateCallRingtone = payload => {
    const conversationId = this.extractConversationId(payload);
    if (!conversationId) return;

    const callStatus = this.resolveCallStatus(payload);
    const isRinging = isActiveWhatsAppRinging({
      status: callStatus,
      timestamp: this.resolveCallTimestamp(payload),
      conversationStatus: this.resolveConversationStatus(payload),
    });
    const assignedToCurrentUser =
      this.isConversationAssignedToCurrentUser(payload);

    if (isRinging && assignedToCurrentUser) {
      this.startCallRingingForConversation(conversationId);
      return;
    }

    this.stopCallRingingForConversation(conversationId);
  };

  syncActiveCallRingtonesFromStore = () => {
    const allConversations =
      this.store?.store?.getters?.getAllConversations || [];
    allConversations.forEach(conversation => {
      this.evaluateCallRingtone(conversation);
    });
  };

  shouldPlayAlert = () => {
    if (this.notificationConfig.playAlertOnlyWhenHidden) {
      return !WindowVisibilityHelper.isWindowVisible();
    }
    return true;
  };

  executeRecurringNotification = () => {
    if (this.store.hasUnreadConversation() && this.shouldPlayAlert()) {
      this.playAudioAlert();
      showBadgeOnFavicon();
    }
    this.resetRecurringTimer();
  };

  clearRecurringTimer = () => {
    if (this.recurringNotificationTimer) {
      clearTimeout(this.recurringNotificationTimer);
    }
  };

  resetRecurringTimer = () => {
    this.clearRecurringTimer();
    this.recurringNotificationTimer = setTimeout(
      this.executeRecurringNotification,
      NOTIFICATION_TIME
    );
  };

  playAudioEvery30Seconds = () => {
    const { audioAlertType, alertIfUnreadConversationExist } =
      this.notificationConfig;

    //  Audio alert is disabled dismiss the timer
    if (audioAlertType.includes('none')) return;

    // If unread conversation flag is disabled, dismiss the timer
    if (!alertIfUnreadConversationExist) return;

    this.resetRecurringTimer();
  };

  shouldNotifyOnMessage = message => {
    const { audioAlertType } = this.notificationConfig;
    if (audioAlertType.includes('none')) return false;

    if (this.isInternalGroupMessage(message)) {
      if (!this.isCurrentUserInternalGroupMember(message)) return false;

      return (
        audioAlertType.includes('all') ||
        audioAlertType.includes(EVENT_TYPES.ASSIGNED) ||
        audioAlertType.includes(EVENT_TYPES.UNASSIGNED)
      );
    }

    if (audioAlertType.includes('all')) return true;

    const assignedToMe = isConversationAssignedToMe(
      message,
      this.currentUser.id
    );
    const isUnassigned = isConversationUnassigned(message);

    const shouldPlayAudio = [];

    if (audioAlertType.includes(EVENT_TYPES.ASSIGNED)) {
      shouldPlayAudio.push(assignedToMe);
    }
    if (audioAlertType.includes(EVENT_TYPES.UNASSIGNED)) {
      shouldPlayAudio.push(isUnassigned);
    }
    if (audioAlertType.includes(EVENT_TYPES.NOTME)) {
      shouldPlayAudio.push(!isUnassigned && !assignedToMe);
    }

    return shouldPlayAudio.some(Boolean);
  };

  onNewMessage = message => {
    // If the user does not have the permission to view the conversation, then dismiss the alert
    // FIX ME: There shouldn't be a new message if the user has no access to the conversation.
    if (!this.store.hasConversationPermission(this.currentUser)) {
      return;
    }

    // If the message is sent by the current user then dismiss the alert
    if (isMessageFromCurrentUser(message, this.currentUser.id)) {
      return;
    }

    if (!this.shouldNotifyOnMessage(message)) {
      return;
    }

    // Internal group messages sent by other users are outgoing, but should alert group members.
    const { message_type: messageType, private: isPrivate } = message;
    const isInternalGroupMessage = this.isInternalGroupMessage(message);
    if (
      messageType !== MESSAGE_TYPE.INCOMING &&
      !isPrivate &&
      !isInternalGroupMessage
    ) {
      return;
    }

    if (WindowVisibilityHelper.isWindowVisible()) {
      // If the user looking at the conversation, then dismiss the alert
      if (this.store.isMessageFromCurrentConversation(message)) {
        return;
      }

      // If the user has disabled alerts when active on the dashboard, the dismiss the alert
      if (this.notificationConfig.playAlertOnlyWhenHidden) {
        return;
      }
    }

    this.playAudioAlert();
    showBadgeOnFavicon();
    this.playAudioEvery30Seconds();
  };

  onConversationUpdated = conversation => {
    this.evaluateCallRingtone(conversation);
  };

  onAssigneeChanged = conversation => {
    this.evaluateCallRingtone(conversation);
  };

  onCallMessageCreated = message => {
    this.evaluateCallRingtone(message);
  };
}

export default new DashboardAudioNotificationHelper(GlobalStore);
